import { Box, Grid } from "@material-ui/core";
import { useEffect, useRef, useState } from "react";
import { BootstrapInput } from "../../../utils/styles";
import customCreditCardStyles from "./CustomCreditCard.styles";
import InvalidCard from "../../../assets/icons/cards/stp_card_unknown.png";
import { getInputMaskNumber, getMaskLength } from "../../../helpers/methods";
import visaImg from "../../../assets/icons/cards/stp_card_visa.png";
import amExpressImg from "../../../assets/icons/cards/stp_card_amex.png";
import mastercardImg from "../../../assets/icons/cards/stp_card_mastercard.png";
import jcbcardImg from "../../../assets/icons/cards/stp_card_jcb.png";
import discoverImg from "../../../assets/icons/cards/stp_card_discover.png";
import dinersImg from "../../../assets/icons/cards/stp_card_diners.png";
import uniPayImg from "../../../assets/icons/cards/stp_card_unionpay.jpg";
import VisaElectronImg from "../../../assets/icons/cards/stp_card_visa_electron.png";
import { getCardType } from "./cardConstant";
import strings from "../../constants/StringConstants";
import CustomMaskComponent from "../CustomMaskComponent/CustomMaskComponent";

interface CustomProps {
  setCardInfo: Function;
  setIsPayBtnEnable: Function;
  acceptedCardBrands: string[];
  isTabSession: boolean;
}

interface cardType {
  cardImg: string;
  error: boolean;
}

const CustomCreditCard = (props: CustomProps) => {
  const classes = customCreditCardStyles();
  const [cardNumber, setCardNumber] = useState({
    type: "",
    mask: "0000 0000 0000 0000",
    value: "",
  });
  const [cardHolderName, setCardHolderName] = useState("");
  const [acceptedCards, setAcceptedCards] = useState<string[]>([]);
  const [monthYear, setMonthYear] = useState({ value: "", error: false });
  const [zip, setZip] = useState("");
  const [cvc, setCvc] = useState({
    mask: "000",
    value: "",
  });
  const [cardType, setCardType] = useState<cardType>({
    cardImg: InvalidCard,
    error: false,
  });

  useEffect(() => {
    const acceptedCardTypes: string[] = props.acceptedCardBrands.filter(
      (cardType: string) => strings.TAB_ACCEPTED_CARDS.includes(cardType)
    );
    setAcceptedCards(acceptedCardTypes);
  }, [props.acceptedCardBrands]);

  useEffect(() => {
    props.setCardInfo({
      type: cardNumber.type,
      name: cardHolderName,
      cardNumber: cardNumber.value,
      monthYear: monthYear.value,
      cvc: cvc.value,
      zip,
    });
    validatePayBtn();
  }, [cardHolderName, cardNumber, monthYear, zip, cvc]);

  useEffect(() => {
    if (
      props.isTabSession &&
      getInputMaskNumber(cardNumber.value).length >=
        getMaskLength(cardNumber.mask)
    ) {
      isCardTypeAccepted();
    }
  }, [cardNumber.value]);

  const validatePayBtn = () => {
    const areFieldsValid =
      getInputMaskNumber(cardNumber.value).length ===
        getMaskLength(cardNumber.mask) &&
      cardHolderName.length >= 2 &&
      getInputMaskNumber(cvc.value).length === getMaskLength(cvc.mask) &&
      getInputMaskNumber(monthYear.value).length === 4 &&
      getInputMaskNumber(zip).length === 5 &&
      !monthYear.error &&
      !cardType.error;
    props.setIsPayBtnEnable(areFieldsValid);
  };

  const updateCardTypes = (number: string) => {
    const { type, cardMask, cvcMask } = getCardType(getInputMaskNumber(number));
    switch (type) {
      case "visa": {
        setCardType({ cardImg: visaImg, error: false });
        break;
      }
      case "mastercard": {
        setCardType({ cardImg: mastercardImg, error: false });
        break;
      }
      case "amex": {
        setCardType({ cardImg: amExpressImg, error: false });
        break;
      }
      case "discover": {
        setCardType({ cardImg: discoverImg, error: false });
        break;
      }
      case "diners_club":
      case "Diners - Carte Blanche": {
        setCardType({ cardImg: dinersImg, error: false });
        break;
      }
      case "jcb": {
        setCardType({ cardImg: jcbcardImg, error: false });
        break;
      }
      case "unionpay": {
        setCardType({ cardImg: uniPayImg, error: false });
        break;
      }
      case "Visa Electron": {
        setCardType({ cardImg: VisaElectronImg, error: false });
        break;
      }
      default: {
        setCardType({
          cardImg: InvalidCard,
          error: props.isTabSession ? cardType.error : true,
        });
      }
    }
    setCardNumber({
      ...cardNumber,
      type,
      mask: cardMask,
      value: number,
    });
    setCvc({
      ...cvc,
      mask: cvcMask,
    });
  };

  const handleCardHolderName = (event: any) => {
    let cardHolderName = event.target.value;
    cardHolderName = cardHolderName.replace(/[^a-zA-Z\s]+/g, "");
    setCardHolderName(cardHolderName);
  };

  const handleOnChange = (value: any, name: string) => {
    if (name === "cardNumber") {
      updateCardTypes(value);
    }
    if (name === "monthYear") {
      validateMonthYear(value);
    }
    if (name === "cvc") {
      setCvc({ ...cvc, value });
    }
    if (name === "zip") {
      setZip(value);
    }
  };

  const validateMonthYear = (value: string) => {
    const expiryMonth = parseInt(getInputMaskNumber(value.split("/")[0]));

    const expiryYear = parseInt(getInputMaskNumber(value.split("/")[1]));
    let isValid = true;

    const todayDate = new Date();
    const presetMonth = parseInt(todayDate.getMonth().toString());
    const presetYear = parseInt(todayDate.getFullYear().toString().slice(-2));
    if (
      presetYear > expiryYear ||
      (presetYear === expiryYear && presetMonth >= expiryMonth) ||
      expiryMonth > 12
    ) {
      isValid = false;
    }
    setMonthYear({ value: value, error: !isValid });
    return isValid;
  };

  const isCardTypeAccepted = () => {
    const isValid: boolean = !!acceptedCards.find((cardName: string) => {
      return cardName.toLowerCase() === cardNumber.type.toLowerCase();
    });
    setCardType({ ...cardType, error: !isValid });
  };

  return (
    <Box marginY={1}>
      <Grid container spacing={1} className={classes.gridContainer}>
        <Grid item sm={12} xs={12} >
          <BootstrapInput
            autoFocus={true}
            placeholder={"Card holder's name"}
            className={classes.customInput}
            value={cardHolderName}
            name="cardHolderName"
            onChange={handleCardHolderName}
            type={"text"}
            id="cardHolderName"
            autoComplete="cc-name"
          />
        </Grid>
        <Grid item sm={12} xs={12} >
          <CustomMaskComponent
            id="creditCardNumber"
            placeholder="Card number"
            mask={cardNumber.mask}
            onChange={handleOnChange}
            className={classes.customInput}
            name="cardNumber"
            autoComplete="cc-number"
            endAdornment={
              <img className={classes.cardTypeImage} src={cardType.cardImg} />
            }
          />
          {props.isTabSession && cardType.error && (
            <Box className={classes.errorMessage}>
              The card is not supported
            </Box>
          )}
        </Grid>
        <Grid item sm={4} xs={4}>
          <CustomMaskComponent
            id="cardExpirationMonth"
            placeholder={"MM / YY"}
            mask={"00 / 00"}
            onChange={handleOnChange}
            className={classes.customInput}
            name="monthYear"
            autoComplete="cc-exp"
          />
        </Grid>
        <Grid item sm={4} xs={4}>
          <CustomMaskComponent
            id="cvc2"
            placeholder={"CVC"}
            mask={cvc.mask}
            onChange={handleOnChange}
            className={classes.customInput}
            name="cvc"
            autoComplete="cc-csc"
          />
        </Grid>
        <Grid item sm={4} xs={4}>
          <CustomMaskComponent
            id="zip"
            placeholder={"ZIP"}
            mask="00000"
            onChange={handleOnChange}
            className={classes.customInput}
            name="zip"
            autoComplete="zip"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default CustomCreditCard;
